import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

const DrillAssignmentForm = ({
  coachId,
  teamId,
  players = [],
  drills = [],
  onSuccess,
  allowBulkAssignment = false,
  // Legacy props for backward compatibility
  player,
  onClose,
}) => {
  // Use legacy props if provided
  const effectivePlayers = player ? [player] : players;
  const effectiveOnSuccess = onSuccess || onClose;

  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedDrill, setSelectedDrill] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(10);
  const [difficulty, setDifficulty] = useState('intermediate');
  const [notes, setNotes] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-select player if only one
  useEffect(() => {
    if (effectivePlayers.length === 1 && !selectedPlayer) {
      setSelectedPlayer(effectivePlayers[0].id);
    }
  }, [effectivePlayers, selectedPlayer]);

  const filteredDrills = drills.filter((drill) => {
    const matchesCategory = !categoryFilter || drill.category === categoryFilter;
    const matchesSearch =
      !searchTerm || drill.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handlePlayerCheckboxToggle = (playerId) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPlayers.length === effectivePlayers.length) {
      setSelectedPlayers([]);
    } else {
      setSelectedPlayers(effectivePlayers.map((p) => p.id));
    }
  };

  const validateForm = () => {
    // Check player selection
    if (!bulkMode && !selectedPlayer) {
      setError('Please select a player');
      return false;
    }

    if (bulkMode && selectedPlayers.length === 0) {
      setError('Please select at least one player');
      return false;
    }

    // Check drill selection
    if (!selectedDrill) {
      setError('Please select a drill');
      return false;
    }

    // Validate sets and reps
    if (sets < 1 || reps < 1) {
      setError('Sets and reps must be positive numbers');
      return false;
    }

    // Validate due date
    if (dueDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(dueDate);

      if (selectedDate < today) {
        setError('Due date cannot be in the past');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const playerIds = bulkMode ? selectedPlayers : [selectedPlayer];

      const assignments = playerIds.map((playerId) => ({
        player_id: playerId,
        drill_id: selectedDrill,
        assigned_by: coachId,
        due_date: dueDate || null,
        sets,
        reps,
        difficulty_level: difficulty,
        coach_notes: notes || null,
      }));

      const { error: insertError } = await supabase.from('assigned_drills').insert(assignments);

      if (insertError) throw insertError;

      // Call success callback
      if (effectiveOnSuccess) {
        setTimeout(() => {
          effectiveOnSuccess();
        }, 1500);
      }
    } catch (err) {
      console.error('Error assigning drills:', err);
      setError(err.message || 'Failed to assign drill');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {error && (
        <div
          style={{
            padding: '12px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {/* Bulk Assignment Toggle */}
      {allowBulkAssignment && effectivePlayers.length > 1 && (
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={bulkMode}
              onChange={(e) => {
                setBulkMode(e.target.checked);
                if (!e.target.checked) {
                  setSelectedPlayers([]);
                }
              }}
            />
            Assign to multiple players
          </label>
        </div>
      )}

      {/* Player Selection */}
      {!bulkMode ? (
        <div>
          <label
            htmlFor="player-select"
            style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
          >
            Select Player *
          </label>
          <select
            id="player-select"
            value={selectedPlayer}
            onChange={(e) => setSelectedPlayer(e.target.value)}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
            }}
            required={!bulkMode}
          >
            <option value="">-- Select a player --</option>
            {effectivePlayers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Select Players *
          </label>
          <div style={{ marginBottom: '8px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={
                  selectedPlayers.length === effectivePlayers.length && effectivePlayers.length > 0
                }
                onChange={handleSelectAll}
              />
              Select All
            </label>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              padding: '8px',
              borderRadius: '4px',
            }}
          >
            {effectivePlayers.map((p) => (
              <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={selectedPlayers.includes(p.id)}
                  onChange={() => handlePlayerCheckboxToggle(p.id)}
                />
                {p.full_name}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div>
        <label
          htmlFor="category-filter"
          style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
        >
          Category
        </label>
        <select
          id="category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="">All Categories</option>
          <option value="batting">Batting</option>
          <option value="fielding">Fielding</option>
          <option value="baserunning">Base Running</option>
          <option value="pitching">Pitching</option>
        </select>
      </div>

      {/* Search Drills */}
      <div>
        <input
          type="text"
          placeholder="Search drills..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        />
      </div>

      {/* Drill Selection */}
      <div>
        <label
          htmlFor="drill-select"
          style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
        >
          Select Drill *
        </label>
        <select
          id="drill-select"
          value={selectedDrill}
          onChange={(e) => setSelectedDrill(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
          required
        >
          <option value="">-- Select a drill --</option>
          {filteredDrills.map((drill) => (
            <option key={drill.id} value={drill.id}>
              {drill.name}
            </option>
          ))}
        </select>
      </div>

      {/* Sets and Reps */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <div>
          <label
            htmlFor="sets-input"
            style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
          >
            Sets
          </label>
          <input
            id="sets-input"
            type="number"
            min="1"
            value={sets}
            onChange={(e) => setSets(parseInt(e.target.value) || 1)}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
            }}
          />
        </div>
        <div>
          <label
            htmlFor="reps-input"
            style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
          >
            Reps
          </label>
          <input
            id="reps-input"
            type="number"
            min="1"
            value={reps}
            onChange={(e) => setReps(parseInt(e.target.value) || 1)}
            style={{
              width: '100%',
              padding: '8px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
            }}
          />
        </div>
      </div>

      {/* Difficulty */}
      <div>
        <label
          htmlFor="difficulty-select"
          style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
        >
          Difficulty
        </label>
        <select
          id="difficulty-select"
          value={difficulty}
          onChange={(e) => setDifficulty(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
      </div>

      {/* Due Date */}
      <div>
        <label
          htmlFor="due-date-input"
          style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
        >
          Due Date
        </label>
        <input
          id="due-date-input"
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        />
      </div>

      {/* Notes */}
      <div>
        <label
          htmlFor="notes-textarea"
          style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}
        >
          Notes (Optional)
        </label>
        <textarea
          id="notes-textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add any special instructions..."
          style={{
            width: '100%',
            padding: '8px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            minHeight: '80px',
            resize: 'vertical',
          }}
        />
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        style={{
          padding: '12px',
          fontSize: '16px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: loading ? '#ccc' : '#007bff',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
        }}
      >
        {loading ? 'Assigning...' : 'Assign Drill'}
      </button>
    </form>
  );
};

export default DrillAssignmentForm;
